<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar (Handles its own mobile overlay logic) -->
    <ComSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <!-- md:ml-64 pushes this entire section to the right of the sidebar on desktop -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar (Sticky) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <ComTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Top Controls -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Communication Dashboard</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Last updated: {{ currentDate }}</p>
          </div>
          <button
            @click="refreshData"
            class="inline-flex items-center justify-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" :class="{'animate-spin': isRefreshing}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>

        <!-- Welcome Profile Card -->
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-8">
          <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div class="flex items-center gap-5">
              <!-- Avatar / Initials Fallback -->
              <div class="h-20 w-20 rounded-full bg-gradient-to-br from-[#035a76] to-[#04698f] flex items-center justify-center text-white text-2xl font-bold shadow-md flex-shrink-0 border-4 border-[#74bd54]/20">
                {{ user?.photo ? '' : getInitials(user?.name) }}
                <img v-if="user?.photo" :src="user.photo" alt="Profile" class="h-full w-full rounded-full object-cover" />
              </div>
              <div>
                <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
                  Welcome back, {{ user?.name || "User" }}
                </h2>
                <p class="text-gray-600 dark:text-gray-300 mt-1 font-medium">
                  {{ user?.position || 'Staff' }} • {{ user?.department || 'Communication' }}
                </p>
                <div class="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-500 dark:text-gray-400">
                  <span class="flex items-center gap-1">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" /></svg>
                    ID: {{ user?.employee_code || 'N/A' }}
                  </span>
                  <span class="hidden sm:inline text-gray-300 dark:text-gray-600">•</span>
                  <span>{{ user?.section || 'N/A' }} • {{ user?.class_name || 'N/A' }}</span>
                </div>
                <p v-if="user?.subjects?.length" class="text-sm text-[#035a76] dark:text-blue-400 mt-1 font-medium">
                  Subjects: {{ user.subjects.map(s => s.name).join(', ') }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4 text-sm w-full lg:w-auto">
              <div class="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 px-4 py-3 rounded-lg">
                <p class="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide">Today's Attendance</p>
                <p class="font-bold text-green-900 dark:text-green-200 mt-1 text-lg">Present</p>
              </div>
              <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 px-4 py-3 rounded-lg">
                <p class="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide">Next Meeting</p>
                <p class="font-bold text-blue-900 dark:text-blue-200 mt-1 text-lg">2:00 PM</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Summary Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div
            v-for="(card, index) in summaryCards"
            :key="index"
            class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
          >
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ card.title }}</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ card.value }}</p>
              </div>
              <div :class="card.iconBg" class="p-3 rounded-lg">
                <component :is="card.icon" class="h-6 w-6" />
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">{{ card.description }}</p>
          </div>
        </div>

        <!-- Main Sections: Tasks & Notifications -->
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          
          <!-- Assigned Tasks -->
          <div class="xl:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <div class="flex justify-between items-center mb-6">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white">My Assigned Tasks</h2>
              <button class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
                View All
              </button>
            </div>

            <div class="space-y-4">
              <div
                v-for="(task, index) in assignedTasks"
                :key="index"
                class="border border-gray-100 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div class="flex justify-between items-start gap-4">
                  <div class="flex-1">
                    <h3 class="font-semibold text-gray-900 dark:text-white">{{ task.title }}</h3>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      Deadline: {{ task.deadline }}
                    </p>
                  </div>
                  <span class="text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap" :class="task.priorityClass">
                    {{ task.priority }}
                  </span>
                </div>

                <div class="mt-4">
                  <div class="flex justify-between text-xs mb-1.5">
                    <span class="text-gray-500 dark:text-gray-400 font-medium">Progress</span>
                    <span class="text-gray-700 dark:text-gray-300 font-semibold">{{ task.progress }}%</span>
                  </div>
                  <div class="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div
                      class="bg-[#74bd54] h-2 rounded-full transition-all duration-500"
                      :style="{ width: task.progress + '%' }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Notifications -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <div class="flex justify-between items-center mb-6">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h2>
              <button class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
                View All
              </button>
            </div>

            <div class="space-y-4">
              <div
                v-for="(note, index) in notifications"
                :key="index"
                class="flex gap-3 p-3 rounded-lg border-l-4"
                :class="note.border"
              >
                <div class="flex-1 min-w-0">
                  <p class="font-semibold text-gray-900 dark:text-white text-sm">{{ note.title }}</p>
                  <p class="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{{ note.message }}</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-2">{{ note.time }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Leave + Schedule -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          <!-- Leave Requests -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <div class="flex justify-between items-center mb-6">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Leave Requests</h2>
              <button class="inline-flex items-center px-4 py-2 bg-[#74bd54] hover:bg-[#65a348] text-white text-sm font-medium rounded-lg shadow-sm transition-colors">
                <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                Request Leave
              </button>
            </div>

            <div class="space-y-3">
              <div
                v-for="(leave, index) in leaveRequests"
                :key="index"
                class="flex justify-between items-center border border-gray-100 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div>
                  <p class="font-semibold text-gray-900 dark:text-white">{{ leave.type }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ leave.dates }}</p>
                </div>
                <span class="text-xs px-2.5 py-1 rounded-full font-semibold" :class="leave.statusClass">
                  {{ leave.status }}
                </span>
              </div>
            </div>
          </div>

          <!-- Upcoming Schedule -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-6">Upcoming Schedule</h2>

            <div class="space-y-4">
              <div
                v-for="(event, index) in upcomingEvents"
                :key="index"
                class="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div class="bg-[#035a76] dark:bg-blue-900/40 text-white dark:text-blue-300 rounded-lg px-3 py-2 text-center min-w-[60px] shadow-sm">
                  <p class="text-lg font-bold leading-none">{{ event.day }}</p>
                  <p class="text-xs font-medium mt-1">{{ event.month }}</p>
                </div>
                <div class="flex-1 pt-1">
                  <p class="font-semibold text-gray-900 dark:text-white">{{ event.title }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    {{ event.time }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ComTopbar from '@/components/com/ComTopbar.vue'
import ComSidebar from '@/components/com/ComSidebar.vue'

const isSidebarOpen = ref(false) // Hidden by default on mobile
const isDarkMode = ref(false)
const isRefreshing = ref(false)
const currentPageTitle = ref('Communication Dashboard')

const user = ref(null)

onMounted(() => {
  const storedUser = localStorage.getItem("user")
  if (storedUser) {
    try {
      user.value = JSON.parse(storedUser)
    } catch (e) {
      console.error("Failed to parse user", e)
    }
  }
})

const getInitials = (name) => {
  if (!name) return "U"
  const parts = name.trim().split(" ")
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

// SVG Icon Components for Summary Cards
const ClipboardIcon = { template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>` }
const CheckCircleIcon = { template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>` }
const CalendarIcon = { template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>` }
const ClockIcon = { template: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>` }

const summaryCards = ref([
  {
    title: 'Pending Tasks',
    value: 5,
    description: 'Tasks awaiting completion',
    icon: ClipboardIcon,
    iconBg: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
  },
  {
    title: 'Completed Tasks',
    value: 18,
    description: 'Successfully completed',
    icon: CheckCircleIcon,
    iconBg: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'
  },
  {
    title: 'Leave Balance',
    value: '12 Days',
    description: 'Annual leave remaining',
    icon: CalendarIcon,
    iconBg: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
  },
  {
    title: 'Pending Requests',
    value: 2,
    description: 'Awaiting approval',
    icon: ClockIcon,
    iconBg: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
  }
])

const assignedTasks = ref([
  {
    title: 'Prepare communication materials',
    deadline: 'April 12, 2026',
    priority: 'High',
    priorityClass: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    progress: 70
  },
  {
    title: 'Review staff announcement draft',
    deadline: 'April 10, 2026',
    priority: 'Medium',
    priorityClass: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    progress: 40
  }
])

const notifications = ref([
  {
    title: 'New Task Assigned',
    message: 'Budget review task assigned by Finance Manager.',
    time: '1 hour ago',
    border: 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
  },
  {
    title: 'Leave Approved',
    message: 'Your annual leave request for May 10-15 has been approved.',
    time: 'Yesterday',
    border: 'border-l-green-500 bg-green-50/50 dark:bg-green-900/10'
  }
])

const leaveRequests = ref([
  {
    type: 'Annual Leave',
    dates: 'May 10 - May 15, 2026',
    status: 'Approved',
    statusClass: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
  },
  {
    type: 'Sick Leave',
    dates: 'April 18 - April 19, 2026',
    status: 'Pending',
    statusClass: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
  }
])

const upcomingEvents = ref([
  {
    day: '12',
    month: 'APR',
    title: 'Communication Team Meeting',
    time: '2:00 PM - 3:30 PM'
  },
  {
    day: '15',
    month: 'APR',
    title: 'Newsletter Submission Deadline',
    time: '5:00 PM'
  }
])

const currentDate = new Date().toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
})

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark')
    localStorage.theme = 'dark'
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.theme = 'light'
  }
}

const refreshData = () => {
  isRefreshing.value = true
  setTimeout(() => {
    console.log('Refreshing communication dashboard...')
    isRefreshing.value = false
  }, 800)
}
</script>